import { Injectable } from '@angular/core';
import { HttpClient, HttpResponse } from '@angular/common/http';
import { ApiProviderService } from './api-provider.service';
import { ApplicationConfigService } from 'services/application-config.service';
import { API_ENDPOINTS } from 'models/enums/endpoints.enum';
import { Observable, of } from 'rxjs';
import { map } from 'rxjs/operators';
import { TransportOrder } from 'models/transport-order.model';
import { Transport } from 'models/transport.model';
import { createRequestOption, mapAsBody } from 'app/shared/util/request-util';
import { DeliveryStep } from 'models/delivery-step.model';
import { isPresent } from 'services/operators';
import { IBaryTableFetchService } from 'services/i-bary-table-fetch-service';
import dayjs from 'dayjs';
import { DATE_FORMAT } from 'models/constants/input.constants';
import { TransportOrderService } from './transport-order.service';
import { DeliveryStepResult } from '../models/delivery-step-result.model';
import { TransportOrderView } from '../models/transport-order-view.model';

export type EntityType = TransportOrderView;
export type EntityResponseType = HttpResponse<TransportOrderView>;
export type EntityArrayResponseType = HttpResponse<TransportOrderView[]>;

@Injectable({ providedIn: 'root' })
export class TransportOrderExtendedService extends TransportOrderService implements IBaryTableFetchService<EntityType> {
  protected override resourceUrl = API_ENDPOINTS.TRANSPORT_ORDER;
  protected confirmDeliveryUrl = API_ENDPOINTS.CONFIRM_DELIVERY;

  constructor(private apiProvider: ApiProviderService, httpClient: HttpClient, applicationConfigService: ApplicationConfigService) {
    super(httpClient, applicationConfigService);
  }

  override create(transportOrder: EntityType): Observable<EntityResponseType> {
    const copy = this.convertDateFromClient(transportOrder);
    return this.apiProvider.post<EntityType>(this.resourceUrl, copy).pipe(map((res: EntityResponseType) => this.convertDateFromServer(res)));
  }

  override update(transportOrder: EntityType): Observable<EntityResponseType> {
    const copy = this.convertDateFromClient(transportOrder);
    return this.apiProvider.put<EntityType>(`${this.resourceUrl}/${transportOrder.id}`, copy).pipe(map((res: EntityResponseType) => this.convertDateFromServer(res)));
  }

  public updateTransportOrderFromTransport(transport: Transport, transportOrderId: number, comment: string | undefined): Observable<TransportOrderView> {
    transport = transport.serialize();
    return this.apiProvider
      .put<EntityType>(`${this.resourceUrl}/${transportOrderId}`, { transport, transportOrderId, comment })
      .pipe(mapAsBody)
      .pipe(map((res: any) => new TransportOrderView(res)));
  }

  override partialUpdate(transportOrder: EntityType): Observable<EntityResponseType> {
    const copy = this.convertDateFromClient(transportOrder);
    return this.apiProvider.patch<EntityType>(`${this.resourceUrl}/${transportOrder.id}`, copy).pipe(map((res: EntityResponseType) => this.convertDateFromServer(res)));
  }

  override find(id: number): Observable<TransportOrderView> {
    return this.apiProvider
      .get<TransportOrder>(`${this.resourceUrl}/${id}`)
      .pipe(mapAsBody)
      .pipe(map((res: any) => new TransportOrderView(res)));
  }

  override query(req?: any): Observable<HttpResponse<TransportOrderView[]>> {
    const options = createRequestOption(req);
    return this.apiProvider.get<EntityType[]>(this.resourceUrl, options).pipe(
      map((res: HttpResponse<TransportOrderView[]>) => {
        return new HttpResponse<TransportOrderView[]>({
          body: (res.body || []).map(v => new TransportOrderView(v)),
          url: res.url as string,
          headers: res.headers,
          status: res.status,
        });
      })
    );
  }

  override delete(id: number): Observable<HttpResponse<{}>> {
    return this.apiProvider.delete<{}>(`${this.resourceUrl}/${id}`);
  }

  sendEdi(transportOrder: EntityType): Observable<EntityResponseType> {
    return this.apiProvider.post<EntityType>(`${this.resourceUrl}/${transportOrder.id}/sendEdi`, {}).pipe(map((res: EntityResponseType) => res));
  }

  getPods(transporterOrder: EntityType): Observable<DeliveryStep[]> {
    return this.apiProvider.get<DeliveryStep[]>(`${API_ENDPOINTS.DELIVERY_STEPS_PODS}/${transporterOrder.id}`).pipe(map(res => res.body!));
  }

  getStepsToShow(transporterOrder: EntityType): Observable<DeliveryStepResult> {
    return this.apiProvider.get<DeliveryStepResult>(`${API_ENDPOINTS.DELIVERY_STEPS_TRANSPORT_ORDER}/${transporterOrder.id}`).pipe(map(res => res.body!));
  }

  // PUBLIC URL
  public findByHash(id: string): Observable<TransportOrderView> {
    return this.apiProvider
      .publicGet<TransportOrderView>(`${this.resourceUrl}/hash/${id}`)
      .pipe(mapAsBody)
      .pipe(map((val: TransportOrderView) => new TransportOrderView(val)));
  }

  // NON ISO URL
  checkTransportIsDeletable(transportOrder: EntityType): Observable<boolean | null> {
    return this.apiProvider.get<boolean>(`${this.resourceUrl}/${transportOrder.id}/is-deletable`).pipe(mapAsBody);
  }

  getHash(id: number): Observable<HttpResponse<string>> {
    return this.apiProvider.publicGet<string>(`${this.resourceUrl}/hash/get/${id}`, { responseType: 'text' });
  }

  confirmDelivery(transportOrderId: number, deliveryDate: Date, podData?: {}): Observable<HttpResponse<any>> {
    return this.apiProvider.post(this.confirmDeliveryUrl(transportOrderId) + '?deliveryDate=' + dayjs(deliveryDate).format(DATE_FORMAT), podData);
  }

  override addTransportOrderToCollectionIfMissing(transportOrderCollection: TransportOrder[], ...transportOrdersToCheck: (TransportOrder | null | undefined)[]): TransportOrder[] {
    const transportOrders: TransportOrder[] = transportOrdersToCheck.filter(isPresent);
    if (transportOrders.length > 0) {
      const transportOrderCollectionIdentifiers = transportOrderCollection.map(transportOrderItem => transportOrderItem.id);
      const transportOrdersToAdd = transportOrders.filter(transportOrderItem => {
        const transportOrderIdentifier = transportOrderItem.id;
        if (transportOrderIdentifier == null || transportOrderCollectionIdentifiers.includes(transportOrderIdentifier)) {
          return false;
        }
        transportOrderCollectionIdentifiers.push(transportOrderIdentifier);
        return true;
      });
      return [...transportOrdersToAdd, ...transportOrderCollection];
    }
    return transportOrderCollection;
  }

  public fillDataToShow(entities: TransportOrderView[]): Observable<TransportOrderView[]> {
    return of(entities);
  }

  override convertDateFromClient(transportOrder: EntityType): EntityType {
    return Object.assign({}, transportOrder, {
      pickupDate: dayjs(transportOrder.pickupDate).isValid() ? dayjs(transportOrder.pickupDate).format(DATE_FORMAT) : undefined,
      deliveryDate: dayjs(transportOrder.deliveryDate).isValid() ? dayjs(transportOrder.deliveryDate).format(DATE_FORMAT) : undefined,
      deliveryMaxDate: dayjs(transportOrder.deliveryMaxDate).isValid() ? dayjs(transportOrder.deliveryMaxDate).format(DATE_FORMAT) : undefined,
      creationDate: dayjs(transportOrder.creationDate).isValid() ? dayjs(transportOrder.creationDate).toJSON() : undefined,
    });
  }

  override convertDateFromServer(res: EntityResponseType): EntityResponseType {
    if (res.body) {
      res.body.pickupDate = res.body.pickupDate ? dayjs(res.body.pickupDate) : undefined;
      res.body.deliveryDate = res.body.deliveryDate ? dayjs(res.body.deliveryDate) : undefined;
      res.body.deliveryMaxDate = res.body.deliveryMaxDate ? dayjs(res.body.deliveryMaxDate) : undefined;
      res.body.creationDate = res.body.creationDate ? dayjs(res.body.creationDate) : undefined;
      if (res.body.pods) {
        this.convertPodsDateFromServer(res.body.pods);
      }
    }
    return res;
  }

  override convertDateArrayFromServer(res: EntityArrayResponseType): EntityArrayResponseType {
    if (res.body) {
      res.body.forEach((transportOrder: TransportOrderView) => {
        transportOrder.pickupDate = transportOrder.pickupDate ? dayjs(transportOrder.pickupDate) : undefined;
        transportOrder.deliveryDate = transportOrder.deliveryDate ? dayjs(transportOrder.deliveryDate) : undefined;
        transportOrder.deliveryMaxDate = transportOrder.deliveryMaxDate ? dayjs(transportOrder.deliveryMaxDate) : undefined;
        transportOrder.creationDate = transportOrder.creationDate ? dayjs(transportOrder.creationDate) : undefined;
        if (transportOrder.pods) {
          this.convertPodsDateFromServer(transportOrder.pods);
        }
      });
    }
    return res;
  }

  convertPodsDateFromServer(pods: DeliveryStep[]): void {
    if (pods) {
      pods.forEach((pod: DeliveryStep) => {
        pod.stepDate = pod.stepDate ? dayjs(pod.stepDate) : undefined;
      });
    }
  }
}
